import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import { getRate, nightCount, isoToday, isoMake, fmtF, curSym, breakfastCost, getMinStay, extraBedCost, stripPhone, validatePhone } from './utils';
import { resolveTypeLabel } from './room-types.js';
import { PRIORITY_CODES, ALL_CODES } from './countryCodes';

const CL = {
  bark: '#3D2B1F', gold: '#C9A96E', goldL: '#E8D5B0',
  cream: '#FFFDF8', sand: '#F5EFE6', olive: '#5A7247',
  sea: '#2B6F8A', stone: '#8B7D6B', terra: '#C0562B',
};

const FONT = {
  display: "'Cormorant Garamond', Georgia, serif",
  body: "'Figtree', 'Helvetica Neue', -apple-system, sans-serif",
};

const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_BG = ['Януари','Февруари','Март','Април','Май','Юни','Юли','Август','Септември','Октомври','Ноември','Декември'];
const DAYS_EN   = ['M','T','W','T','F','S','S'];
const DAYS_BG   = ['П','В','С','Ч','П','С','Н'];
const PHONE_PLACEHOLDERS = { '+30': '697 123 4567', '+359': '887 123 456', '+44': '7911 123456' };


const T = {
  en: {
    yourStay: 'Your stay', selectDates: 'Select your dates',
    chooseRoomTypes: 'Choose room types', yourDetails: 'Your details',
    adults: 'Adults', children: 'Children', childAge: 'Child {n} age',
    addAnotherRoom: '+ Add another room',
    selectCheckOut: 'Now select your check-out date',
    fullName: 'Full name', preferredContact: 'Preferred contact',
    phoneNumber: 'Phone number', email: 'Email', message: 'Message',
    optional: '(optional)', required: '*',
    notRobot: "I'm not a robot",
    privacy: 'Privacy', cloudflare: 'Cloudflare',
    requestBooking: 'Request booking', sending: 'Sending…',
    estimatedTotal: 'Estimated total',
    nights: 'nights', night: 'night',
    perNight: 'per night', sleepsUpTo: 'Sleeps up to',
    bookDirectNote: 'Always book direct. Best price, no fees, straight from the hosts.',
    poweredBy: 'Powered by Tavelo',
    taveloLead: 'Hotel runs on Tavelo.',
    taveloSub: 'A quiet, all-in-one tool for small hotels.',
    buildPage: 'Build a page like this →',
    openInMaps: 'Open in Google Maps →',
    successTitle: 'Thank you, {name}',
    successBody: 'Your booking request has been sent. The hotel will confirm directly with you within a few hours.',
    stay: 'Stay', roomS: 'Room', guests: 'Guests', total: 'Total',
    whatsappHotel: '💬 WhatsApp the hotel',
    countryCode: 'Country code',
    searchCountry: 'Search by name or code…',
    popular: 'Popular', allCountries: 'All countries', otherCountry: '🌍 Other country',
    placeholderName: 'Your full name',
    placeholderEmail: 'your@email.com',
    placeholderMessage: 'Any special requests or questions…',
    placeholderCustomCode: '+XX',
    postNote: 'Rooms shown reflect confirmed bookings only. This is a request — the hotel will confirm with you directly within hours.',
    phone: 'Phone', whatsapp: 'WhatsApp', viber: 'Viber',
    checkingAvailability: 'Checking availability…',
    noRoomsAvailable: 'No rooms available for these dates. Please try different dates.',
    minStay: 'Minimum stay for your selected dates is {n} nights.',
    noRoomTypeFor: 'No room type available for this configuration. Please adjust the number of guests.',
    addBreakfast: 'Add breakfast',
    babyCot: '👶 Baby cot',
    cotMeta: 'Children under 2 · On request · No charge',
    sofaBed: '🛋 Sofa bed',
    specialOccasion: '🎉 Special occasion',
    selected: '✓ Selected',
    limitedAvailability: 'Limited availability — other requests are pending for these dates.',
    breakfastIncluded: '☕ Breakfast included',
    notFound: 'Hotel not found',
    notFoundSub: 'Check the link and try again.',
    loading: 'Loading…',
    errEnterName: 'Please enter your name.',
    errPickContact: "Please select how you'd like to be contacted.",
    errEnterPhone: 'Please enter your phone number.',
    errEnterDial: 'Please enter your country dial code.',
    errLetters: 'Please enter digits only in your phone number.',
    errInvalid: 'Please enter a valid phone number.',
    errEmail: 'Please enter your email address.',
    errLetters_inline: 'Please enter digits only',
    errInvalid_inline: 'Please enter a valid phone number',
    errGeneric: 'Something went wrong. Please try again.',
    errNotifyFailed: "We couldn't reach the hotel right now. Please try again in a moment, or contact them directly.",
    errSecurityCheck: 'Security check failed. Please reload the page and try again.',
    unavailableTitle: 'This hotel is not accepting bookings online right now.',
    unavailableBody:  'Please contact them directly.',
    months: MONTHS_EN, days: DAYS_EN,
    nightFmt: (n) => `${n} ${n === 1 ? 'night' : 'nights'}`,
    adultsFmt: (n) => `${n} ${n === 1 ? 'adult' : 'adults'}`,
    childrenFmt: (n) => n > 0 ? `, ${n} ${n === 1 ? 'child' : 'children'}` : '',
    age: 'age',
  },
  bg: {
    yourStay: 'Вашият престой', selectDates: 'Изберете дати',
    chooseRoomTypes: 'Изберете тип стая', yourDetails: 'Вашите данни',
    adults: 'Възрастни', children: 'Деца', childAge: 'Възраст на дете {n}',
    addAnotherRoom: '+ Добави още стая',
    selectCheckOut: 'Сега изберете датата на напускане',
    fullName: 'Пълно име', preferredContact: 'Предпочитан контакт',
    phoneNumber: 'Телефонен номер', email: 'Имейл', message: 'Съобщение',
    optional: '(по избор)', required: '*',
    notRobot: 'Не съм робот',
    privacy: 'Поверителност', cloudflare: 'Cloudflare',
    requestBooking: 'Заявете резервация', sending: 'Изпращане…',
    estimatedTotal: 'Очаквана сума',
    nights: 'нощувки', night: 'нощувка',
    perNight: 'на нощувка', sleepsUpTo: 'Настанява до',
    bookDirectNote: 'Резервирайте директно. Най-добра цена, без такси, от самите стопани.',
    poweredBy: 'Създадено с Tavelo',
    taveloLead: 'Хотелът работи с Tavelo.',
    taveloSub: 'Ненатрапчиво решение „всичко в едно" за малки хотели.',
    buildPage: 'Направете страница като тази →',
    openInMaps: 'Отвори в Google Maps →',
    successTitle: 'Благодарим, {name}',
    successBody: 'Заявката е изпратена. Хотелът ще Ви потвърди директно в рамките на няколко часа.',
    stay: 'Престой', roomS: 'Стая', guests: 'Гости', total: 'Общо',
    whatsappHotel: '💬 WhatsApp до хотела',
    countryCode: 'Код на държавата',
    searchCountry: 'Търсене по име или код…',
    popular: 'Популярни', allCountries: 'Всички държави', otherCountry: '🌍 Друга държава',
    placeholderName: 'Вашето пълно име',
    placeholderEmail: 'your@email.com',
    placeholderMessage: 'Специални желания или въпроси…',
    placeholderCustomCode: '+XX',
    postNote: 'Показаните стаи отразяват само потвърдени резервации. Това е заявка. Хотелът ще Ви потвърди директно до няколко часа.',
    phone: 'Телефон', whatsapp: 'WhatsApp', viber: 'Viber',
    checkingAvailability: 'Проверка на наличност…',
    noRoomsAvailable: 'Няма свободни стаи за тези дати. Опитайте други дати.',
    minStay: 'Минималният престой за избраните дати е {n} нощувки.',
    noRoomTypeFor: 'Няма подходяща стая за този брой гости. Моля, променете броя им.',
    addBreakfast: 'Добави закуска',
    babyCot: '👶 Бебешко легло',
    cotMeta: 'За деца под 2 г. · По заявка · Без такса',
    sofaBed: '🛋 Разтегателен диван',
    specialOccasion: '🎉 Специален повод',
    selected: '✓ Избрано',
    limitedAvailability: 'Ограничена наличност: има чакащи заявки за тези дати.',
    breakfastIncluded: '☕ Включена закуска',
    notFound: 'Хотелът не е намерен',
    notFoundSub: 'Проверете линка и опитайте отново.',
    loading: 'Зарежда…',
    errEnterName: 'Моля, въведете вашето име.',
    errPickContact: 'Моля, изберете как искате да се свържат с вас.',
    errEnterPhone: 'Моля, въведете телефонен номер.',
    errEnterDial: 'Моля, въведете международния код.',
    errLetters: 'Моля, въведете само цифри.',
    errInvalid: 'Моля, въведете валиден телефонен номер.',
    errEmail: 'Моля, въведете имейл адрес.',
    errLetters_inline: 'Моля, въведете само цифри',
    errInvalid_inline: 'Моля, въведете валиден номер',
    errGeneric: 'Нещо се обърка. Опитайте отново.',
    errNotifyFailed: 'В момента не можем да се свържем с хотела. Моля, опитайте отново след малко или го потърсете директно.',
    errSecurityCheck: 'Проверката за сигурност не успя. Моля, презаредете страницата и опитайте отново.',
    unavailableTitle: 'Този хотел в момента не приема онлайн резервации.',
    unavailableBody:  'Моля, свържете се директно с хотела.',
    months: MONTHS_BG, days: DAYS_BG,
    nightFmt: (n) => `${n} ${n === 1 ? 'нощувка' : 'нощувки'}`,
    adultsFmt: (n) => `${n} ${n === 1 ? 'възрастен' : 'възрастни'}`,
    childrenFmt: (n) => n > 0 ? `, ${n} ${n === 1 ? 'дете' : 'деца'}` : '',
    age: 'възр.',
  },
};

function fillTemplate(s, vars) {
  return s.replace(/\{(\w+)\}/g, (_, k) => vars[k] != null ? String(vars[k]) : '');
}

// ── Atoms ──────────────────────────────────────────────────────────────────────
function Lbl({ children, optional, dim }) {
  return (
    <div style={{
      fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6,
      textTransform: 'uppercase', color: dim ? CL.stone : CL.stone,
      marginBottom: 6, fontFamily: FONT.body,
    }}>
      {children}
      {optional && (
        <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 4 }}>
          {optional}
        </span>
      )}
    </div>
  );
}

function StepHeader({ n, title, done }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div style={{
        width: 30, height: 30, borderRadius: 999,
        border: '1.5px solid ' + (done ? CL.olive : CL.gold),
        background: done ? CL.olive : 'transparent',
        color: done ? '#fff' : CL.gold,
        fontFamily: FONT.display, fontSize: done ? 14 : 17, fontWeight: 500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {done ? '✓' : n}
      </div>
      <div style={{
        fontFamily: FONT.display, fontSize: 20, fontWeight: 500,
        color: CL.bark, lineHeight: 1.05,
      }}>{title}</div>
    </div>
  );
}

function Stepper({ value, min = 0, max = 20, onChange }) {
  const btn = (disabled) => ({
    width: 32, height: 34, background: 'transparent',
    border: 'none', color: disabled ? '#C4B5A8' : CL.bark,
    fontSize: 18, lineHeight: 1, cursor: disabled ? 'default' : 'pointer',
    fontFamily: 'inherit',
  });
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'stretch',
      border: '1px solid ' + CL.goldL, borderRadius: 8,
      overflow: 'hidden', background: CL.cream,
    }}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} style={btn(value <= min)}>−</button>
      <div style={{
        minWidth: 42, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT.display, fontSize: 16, fontWeight: 500, color: CL.bark,
        borderLeft: '1px solid ' + CL.goldL, borderRight: '1px solid ' + CL.goldL,
      }}>{value}</div>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} style={btn(value >= max)}>+</button>
    </div>
  );
}

// ── Calendar ───────────────────────────────────────────────────────────────────
function CalendarPicker({ checkIn, checkOut, onSelect, lang }) {
  const t = T[lang];
  const today = isoToday(new Date());
  const [vm, setVm] = useState(() => {
    if (checkIn) { const d = new Date(checkIn + 'T12:00:00'); return [d.getFullYear(), d.getMonth()]; }
    return [new Date().getFullYear(), new Date().getMonth()];
  });
  const [vY, vM] = vm;
  const dim   = new Date(vY, vM + 1, 0).getDate();
  const fdow  = (new Date(vY, vM, 1).getDay() + 6) % 7;
  const nc    = nightCount(checkIn, checkOut);

  function tap(ds) {
    if (ds < today) return;
    if (!checkIn || (checkIn && checkOut)) { onSelect(ds, null); }
    else if (ds <= checkIn) { onSelect(ds, null); }
    else { onSelect(checkIn, ds); }
  }

  const navBtn = {
    width: 26, height: 26, border: 'none', background: 'transparent',
    color: CL.bark, fontSize: 18, cursor: 'pointer', lineHeight: 1,
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button type="button" onClick={() => setVm(vM === 0 ? [vY-1, 11] : [vY, vM-1])} style={navBtn}>‹</button>
        <span style={{ fontFamily: FONT.display, fontSize: 18, fontWeight: 500, color: CL.bark }}>{t.months[vM]} {vY}</span>
        <button type="button" onClick={() => setVm(vM === 11 ? [vY+1, 0] : [vY, vM+1])} style={navBtn}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4, fontFamily: FONT.body }}>
        {t.days.map((d, i) => (
          <div key={i} style={{
            textAlign: 'center', fontSize: 9, color: CL.stone,
            fontWeight: 700, letterSpacing: 1.4, padding: '4px 0', textTransform: 'uppercase',
          }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {Array(fdow).fill(null).map((_, i) => <div key={'e'+i} />)}
        {Array.from({ length: dim }, (_, i) => {
          const day = i + 1;
          const ds  = isoMake(vY, vM + 1, day);
          const past = ds < today;
          const isCi = ds === checkIn, isCo = ds === checkOut;
          const inR  = checkIn && checkOut && ds > checkIn && ds < checkOut;
          let bg = 'transparent', cl = past ? '#C8BCA8' : CL.bark;
          let br = 6;
          if (isCi)      { bg = CL.olive; cl = '#fff'; br = '6px 0 0 6px'; }
          else if (isCo) { bg = CL.olive; cl = '#fff'; br = '0 6px 6px 0'; }
          else if (inR)  { bg = '#EFE7D2'; cl = CL.bark; br = 0; }
          return (
            <button key={day} type="button" onClick={() => tap(ds)} disabled={past} style={{
              height: 32, background: bg, color: cl, border: 'none',
              borderRadius: br, fontSize: 13,
              fontFamily: FONT.display, fontWeight: 500,
              cursor: past ? 'default' : 'pointer', opacity: past ? 0.5 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{day}</button>
          );
        })}
      </div>
      {checkIn && (
        <div style={{ marginTop: 10, display: 'flex', gap: 12, fontSize: 13, color: CL.bark, flexWrap: 'wrap' }}>
          <div><span style={{ color: CL.stone }}>{lang === 'bg' ? 'Настаняване:' : 'Check-in:'}</span> <strong style={{ fontFamily: FONT.display, fontWeight: 500 }}>{fmtF(checkIn)}</strong></div>
          {checkOut && <div><span style={{ color: CL.stone }}>{lang === 'bg' ? 'Напускане:' : 'Check-out:'}</span> <strong style={{ fontFamily: FONT.display, fontWeight: 500 }}>{fmtF(checkOut)}</strong></div>}
          {checkOut && <div style={{ color: CL.olive, fontWeight: 700 }}>{t.nightFmt(nc)}</div>}
        </div>
      )}
      {checkIn && !checkOut && (
        <div style={{ marginTop: 8, fontSize: 12, color: CL.stone }}>{t.selectCheckOut}</div>
      )}
    </div>
  );
}

// ── Country picker bottom-sheet modal ──────────────────────────────────────────
function CountryPickerModal({ open, onClose, selectedKey, onPick, lang }) {
  const t = T[lang];
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);
  if (!open) return null;

  const ql = q.trim().toLowerCase();
  const match = (c) => !ql || c.name.toLowerCase().includes(ql) || c.code.toLowerCase().includes(ql);
  const popular = PRIORITY_CODES.filter(match);
  const all     = ALL_CODES.filter(match);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(61,43,31,0.55)',
      backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
      zIndex: 100, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: CL.cream, borderRadius: '18px 18px 0 0',
        padding: '18px 18px 22px', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontFamily: FONT.display, fontSize: 22, fontWeight: 500, color: CL.bark }}>{t.countryCode}</span>
          <button type="button" onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 22, color: CL.stone, cursor: 'pointer', lineHeight: 1,
          }}>×</button>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 12px', border: '1.5px solid ' + CL.goldL,
          borderRadius: 10, background: '#FFFEF9', marginBottom: 12,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={CL.stone} strokeWidth="2.2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder={t.searchCountry}
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: 14, color: CL.bark, fontFamily: FONT.body,
            }}
          />
        </div>

        <div style={{ overflowY: 'auto', maxHeight: '60vh', margin: '0 -4px' }}>
          {popular.length > 0 && (
            <>
              <div style={sectionStyle}>{t.popular}</div>
              {popular.map((c, i) => (
                <CountryRow key={'p'+i} c={c} selectedKey={selectedKey}
                  rowKey={`p${i}-${c.code}-${c.name}`} onPick={onPick} />
              ))}
            </>
          )}
          {all.length > 0 && (
            <>
              <div style={sectionStyle}>{t.allCountries}</div>
              {all.map((c, i) => (
                <CountryRow key={'a'+i} c={c} selectedKey={selectedKey}
                  rowKey={`a${i}-${c.code}-${c.name}`} onPick={onPick} />
              ))}
            </>
          )}
          <div
            onClick={() => onPick({ code: 'other', flag: '🌍', name: t.otherCountry, key: 'other' })}
            style={{
              ...rowStyle,
              background: selectedKey === 'other' ? CL.sand : 'transparent',
            }}>
            <span style={{ fontSize: 22, lineHeight: 1, width: 30, textAlign: 'center' }}>🌍</span>
            <span style={{ flex: 1, fontSize: 13.5, color: CL.bark, fontWeight: 500 }}>{t.otherCountry.replace('🌍 ', '')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const sectionStyle = {
  padding: '6px 8px', fontSize: 9.5, letterSpacing: 1.4,
  color: CL.stone, textTransform: 'uppercase', fontWeight: 700,
};

const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 8px', borderRadius: 8, cursor: 'pointer',
};

function CountryRow({ c, selectedKey, rowKey, onPick }) {
  const isOn = selectedKey === rowKey;
  return (
    <div onClick={() => onPick({ code: c.code, flag: c.flag, name: c.name, key: rowKey })} style={{
      ...rowStyle, background: isOn ? CL.sand : 'transparent',
    }}>
      <span style={{ fontSize: 22, lineHeight: 1, width: 30, textAlign: 'center' }}>{c.flag}</span>
      <span style={{ flex: 1, fontSize: 13.5, color: CL.bark, fontWeight: 500, fontFamily: FONT.body }}>{c.name}</span>
      <span style={{ fontFamily: FONT.display, fontSize: 15, color: CL.bark, fontWeight: 500 }}>{c.code}</span>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function typesForRoom(config, availableRooms, selectedTypes, myIdx, checkIn) {
  const byType = {};
  for (const r of availableRooms) {
    if (!byType[r.type]) byType[r.type] = [];
    byType[r.type].push(r);
  }
  for (const t of Object.keys(byType)) {
    byType[t].sort((a, b) => getRate(a, checkIn) - getRate(b, checkIn));
  }

  const claimed = {};
  selectedTypes.forEach((t, i) => { if (i !== myIdx && t) claimed[t] = (claimed[t] || 0) + 1; });

  const total = config.adults + config.children;

  return Object.entries(byType)
    .map(([type, rooms]) => {
      const remaining = rooms.length - (claimed[type] || 0);
      if (remaining <= 0) return null;

      const maxAdults   = rooms[0].maxAdults;
      const maxChildren = rooms[0].maxChildren;
      const maxCap      = Math.max(...rooms.map(r => r.capacity));

      if (total > maxCap) return null;
      if (maxAdults   !== null && config.adults   > maxAdults)   return null;
      if (maxChildren !== null && config.children > maxChildren) return null;

      const cheapest = Math.min(...rooms.map(r => getRate(r, checkIn)));
      return { type, maxCap, maxAdults, cheapest, remaining };
    })
    .filter(Boolean)
    .sort((a, b) => a.cheapest - b.cheapest);
}


// ── Hero ──────────────────────────────────────────────────────────────────────
// Tavelo signature hero. Two variants:
//   - Default (when properties.hero_image_url is null): K3 atmospheric olive
//     composition — pure CSS, four layers (base / glow / vignette / content).
//     The glow layer drifts horizontally on a 30s cycle.
//   - Photo (when properties.hero_image_url is non-null): full-bleed <img> plus
//     a top-to-bottom olive gradient overlay (rgba(46,58,40,0.55) at the bottom)
//     so the centred content stays legible. No glow-drift motion in this variant.
// Centred content (hotel name, gold rule, Google reviews) and the lang
// switcher are identical across both variants.

function Hero({ property, lang, setLang }) {
  const reviewsCount  = property?.google_reviews_count;
  const reviewsRating = property?.google_reviews_rating;
  const showReviews   = reviewsCount != null && reviewsRating != null;
  const heroPhotoUrl  = property?.hero_image_url || null;

  const segBtn = (on) => ({
    background: on ? CL.bark : 'transparent', color: on ? CL.gold : CL.bark,
    border: 'none', padding: '0 10px', height: 24,
    fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
    cursor: 'pointer', fontFamily: FONT.body, textTransform: 'uppercase',
  });

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      height: 340, background: '#5A7247',
    }}>
      {heroPhotoUrl ? (
        <>
          {/* photo variant — full-bleed image + olive bottom gradient for legibility */}
          <img
            src={heroPhotoUrl}
            alt={property?.name || ''}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', zIndex: 0,
            }}
          />
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
            background: 'linear-gradient(180deg, rgba(46,58,40,0) 35%, rgba(46,58,40,0.55) 100%)',
          }} />
        </>
      ) : (
        <>
          {/* K6 default — base gradient backdrop */}
          <div style={{
            position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
            background: 'linear-gradient(180deg, #7A8C66 0%, #5A7247 60%, #445733 100%)',
          }} />

          {/* K6 detail — architectural light bars (static, no animation) */}
          <div style={{
            position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
            background: `repeating-linear-gradient(170deg,
              transparent 0px, transparent 28px,
              rgba(232,213,176,0.06) 28px, rgba(232,213,176,0.06) 31px,
              transparent 31px, transparent 56px)`,
          }} />

          {/* K6 glow — warm static highlight in the upper left */}
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
            background: 'radial-gradient(ellipse 80% 50% at 30% 30%, rgba(232,213,176,0.18), transparent 65%)',
          }} />

          {/* K6 vignette — focus to centre, type contrast */}
          <div style={{
            position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none',
            background: 'radial-gradient(ellipse 130% 100% at 50% 60%, transparent 55%, rgba(20,28,16,0.32) 100%)',
          }} />
        </>
      )}

      {/* lang switcher — own zIndex layer, clickable */}
      <div style={{ position: 'absolute', top: 14, right: 16, zIndex: 5 }}>
        <span style={{
          display: 'inline-flex', border: '1px solid ' + CL.gold,
          borderRadius: 5, overflow: 'hidden',
          background: 'rgba(255,253,248,0.88)', backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)', height: 24,
        }}>
          <button type="button" onClick={() => setLang('en')} style={segBtn(lang === 'en')}>EN</button>
          <button type="button" onClick={() => setLang('bg')} style={segBtn(lang === 'bg')}>BG</button>
        </span>
      </div>

      {/* content — centred composition over the layers */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 4,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-end',
        textAlign: 'center', padding: '36px 28px 30px',
        color: '#fff',
      }}>
        <div style={{
          fontFamily: FONT.display, fontWeight: 500,
          fontSize: 42, letterSpacing: '0.4px',
          color: '#FFFEF9', lineHeight: 1.05, marginBottom: 10,
          textShadow: '0 2px 16px rgba(20,30,15,0.45)',
        }}>{property?.name}</div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
        }}>
          <span style={{ height: 1, width: 36, background: CL.gold }} />
          <span style={{ width: 6, height: 6, background: CL.gold, transform: 'rotate(45deg)', display: 'inline-block' }} />
          <span style={{ height: 1, width: 36, background: CL.gold }} />
        </div>

        {showReviews && (
          <div style={{
            marginTop: 12,
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 11.5, color: CL.gold, fontWeight: 600,
          }}>
            <span style={{ color: CL.gold, letterSpacing: 1 }}>★★★★★</span>
            <span style={{ color: '#FFFEF9', fontWeight: 500 }}>
              {Number(reviewsRating).toFixed(1)} · {reviewsCount} Google reviews
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Direct-booking note ───────────────────────────────────────────────────────
// Flush against the bottom of the hero — full-width cream band, gold eyebrow,
// Cormorant body, no icon, no green. Reads as the second movement of the hero
// composition.
function DirectBookingNote({ lang }) {
  const t = T[lang];
  return (
    <div style={{
      margin: 0, padding: '18px 22px',
      background: CL.cream, textAlign: 'center',
      borderBottom: '1px solid ' + CL.goldL,
    }}>
      <div style={{
        fontSize: 9.5, letterSpacing: 2.5, textTransform: 'uppercase',
        fontWeight: 700, color: CL.gold, marginBottom: 8,
      }}>— TAVELO —</div>
      <div style={{
        fontFamily: FONT.display, fontSize: 17, fontWeight: 500,
        color: CL.bark, lineHeight: 1.35,
        maxWidth: 300, margin: '0 auto',
      }}>{t.bookDirectNote}</div>
    </div>
  );
}

// ── Tavelo footer block + map ─────────────────────────────────────────────────
function FooterBlock({ property, lang }) {
  const t = T[lang];
  const mapsHref = property?.google_maps_link;
  const embedSrc = mapsHref
    ? `https://maps.google.com/maps?q=${encodeURIComponent(property?.name || '')}&z=15&output=embed`
    : null;
  return (
    <div style={{ background: CL.sand, padding: '14px 14px 24px' }}>
      {mapsHref && (
        <>
          <div style={{
            height: 200, borderRadius: 12, border: '1px solid ' + CL.goldL,
            position: 'relative', overflow: 'hidden', marginBottom: 8,
            background: CL.sand,
          }}>
            {embedSrc && (
              <iframe
                src={embedSrc}
                title={`Map of ${property?.name || ''}`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
              />
            )}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '10px 12px', pointerEvents: 'none',
              background: 'linear-gradient(0deg, rgba(61,43,31,0.78) 0%, transparent 100%)',
              color: '#FFFEF9', fontSize: 11, fontWeight: 600,
            }}>
              <strong style={{
                display: 'block', fontFamily: FONT.display, fontSize: 14,
                fontWeight: 500, marginBottom: 1,
              }}>{property?.name}</strong>
            </div>
          </div>
          <a href={mapsHref} target="_blank" rel="noopener noreferrer" style={{
            display: 'block', textAlign: 'center', marginBottom: 14,
            fontSize: 11, color: CL.gold, fontWeight: 600,
            letterSpacing: 0.6, textTransform: 'uppercase',
            textDecoration: 'none',
          }}>{t.openInMaps}</a>
        </>
      )}

      <div style={{
        padding: 14, background: CL.cream,
        border: '1px solid ' + CL.goldL, borderRadius: 12, textAlign: 'center',
      }}>
        <div style={{
          fontSize: 10.5, color: CL.stone, letterSpacing: 1.2,
          textTransform: 'uppercase', fontWeight: 600, marginBottom: 8,
        }}>
          <span style={{
            display: 'inline-block', width: 5, height: 5,
            background: CL.gold, transform: 'rotate(45deg)',
            marginRight: 6, verticalAlign: 'middle',
          }} />
          {t.poweredBy}
        </div>
        <div style={{
          fontFamily: FONT.display, fontSize: 16, fontWeight: 500,
          color: CL.bark, lineHeight: 1.4, marginBottom: 8,
        }}>
          {t.taveloLead}
          <span style={{ display: 'block', fontSize: 14, color: CL.stone, fontWeight: 400, marginTop: 2 }}>
            {t.taveloSub}
          </span>
        </div>
        <a href="https://tavelo.app" target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-block', fontSize: 11, color: CL.gold,
          fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase',
          textDecoration: 'none', borderBottom: '1px solid ' + CL.gold, paddingBottom: 1,
        }}>{t.buildPage}</a>
      </div>
    </div>
  );
}

// ── Sticky total band ─────────────────────────────────────────────────────────
// Pinned to viewport bottom while the user scrolls. Placed at the END of the
// page wrapper's document flow so its natural position is page-bottom — which
// makes both the live behavior (always visible) and fullPage screenshots
// (band sits at the bottom of the captured image) render correctly.
function StickyTotal({ amount, meta, lang }) {
  const t = T[lang];
  return (
    <div style={{
      position: 'sticky', bottom: 0, zIndex: 20,
      background: CL.bark, color: CL.cream,
      padding: '14px 16px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderTop: '1px solid rgba(201,169,110,0.3)',
      boxShadow: '0 -8px 16px rgba(61,43,31,0.18)',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{
          fontSize: 9.5, letterSpacing: 1.6, color: CL.gold,
          textTransform: 'uppercase', fontWeight: 700,
        }}>{t.estimatedTotal}</span>
        <span style={{ fontSize: 11, color: '#D9CDB7', marginTop: 2 }}>{meta}</span>
      </div>
      <span style={{
        fontFamily: FONT.display, fontSize: 24, fontWeight: 500, lineHeight: 1,
      }}>{amount}</span>
    </div>
  );
}

// ── Success screen (replaces page) ────────────────────────────────────────────
function SuccessScreen({ property, summary, lang }) {
  const t = T[lang];
  const phone    = property?.phone || property?.whatsapp_phone;
  const whatsapp = property?.whatsapp_phone || property?.phone;
  const waNumber = whatsapp ? whatsapp.replace(/[^\d+]/g, '') : '';
  const waUrl    = waNumber ? `https://wa.me/${waNumber.replace(/^\+/, '')}` : '';
  const telUrl   = phone ? `tel:${phone.replace(/\s+/g, '')}` : '';

  return (
    <div style={{ fontFamily: FONT.body, background: CL.sand, minHeight: '100vh' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div data-testid="booking-success" style={{
          padding: '48px 24px', textAlign: 'center',
          background: CL.sand, minHeight: 600,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: 999, background: CL.olive,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, marginBottom: 18,
          }}>✓</div>
          <div style={{
            fontFamily: FONT.display, fontSize: 32, fontWeight: 500,
            color: CL.bark, lineHeight: 1.05, marginBottom: 6,
          }}>{fillTemplate(t.successTitle, { name: summary.firstName })}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 16px' }}>
            <span style={{ height: 1, width: 36, background: CL.gold }} />
            <span style={{ width: 6, height: 6, background: CL.gold, transform: 'rotate(45deg)', display: 'inline-block' }} />
            <span style={{ height: 1, width: 36, background: CL.gold }} />
          </div>
          <div style={{
            fontSize: 14, color: CL.bark, lineHeight: 1.55,
            maxWidth: 280, margin: '0 auto 18px',
          }}>{t.successBody}</div>

          <div style={{
            background: CL.cream, border: '1px solid ' + CL.goldL,
            borderRadius: 12, padding: 14, textAlign: 'left',
            fontSize: 13, color: CL.bark, maxWidth: 300, width: '100%',
            margin: '0 auto 18px',
          }}>
            {[
              { lbl: t.stay,    val: summary.stayLine, sub: summary.nightsLine, valColor: CL.bark },
              { lbl: t.roomS,   val: summary.roomLine },
              { lbl: t.guests,  val: summary.guestsLine },
              { lbl: t.total,   val: summary.totalLine, valColor: CL.olive },
            ].map((row, i) => (
              <div key={i} style={{
                padding: '5px 0', display: 'flex', justifyContent: 'space-between',
                borderTop: i === 0 ? 'none' : '1px solid ' + CL.goldL,
              }}>
                <span style={{
                  color: CL.stone, fontSize: 11, letterSpacing: 1,
                  textTransform: 'uppercase', fontWeight: 700,
                }}>{row.lbl}</span>
                <span style={{
                  fontFamily: FONT.display, fontSize: 15, fontWeight: 500,
                  color: row.valColor || CL.bark, textAlign: 'right',
                }}>
                  {row.val}
                  {row.sub && (
                    <small style={{
                      display: 'block', fontFamily: FONT.body, fontSize: 11,
                      color: CL.stone, fontWeight: 400,
                    }}>{row.sub}</small>
                  )}
                </span>
              </div>
            ))}
          </div>

          {(waUrl || telUrl) && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 6 }}>
              {waUrl && (
                <a href={waUrl} target="_blank" rel="noopener noreferrer" style={{
                  padding: '9px 16px', background: CL.bark, color: CL.cream,
                  borderRadius: 999, fontSize: 12, fontWeight: 600,
                  textDecoration: 'none', letterSpacing: 0.4,
                }}>{t.whatsappHotel}</a>
              )}
              {telUrl && (
                <a href={telUrl} style={{
                  padding: '9px 16px', background: CL.cream, color: CL.bark,
                  border: '1px solid ' + CL.goldL, borderRadius: 999,
                  fontSize: 12, fontWeight: 600, textDecoration: 'none', letterSpacing: 0.4,
                }}>📞</a>
              )}
            </div>
          )}
        </div>

        <FooterBlock property={property} lang={lang} />
      </div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function BookingPage() {
  const { slug } = useParams();
  const [property, setProperty]   = useState(null);
  const [notFound, setNotFound]   = useState(false);

  const [lang, _setLang] = useState(() => {
    try {
      const saved = sessionStorage.getItem('tavelo_booking_lang');
      if (saved === 'en' || saved === 'bg') return saved;
    } catch {}
    return 'en';
  });
  function setLang(l) {
    _setLang(l);
    try { sessionStorage.setItem('tavelo_booking_lang', l); } catch {}
  }
  const t = T[lang];

  // Step 1
  const [roomConfigs, setRoomConfigs] = useState([{ adults: 2, children: 0, childAges: [], breakfast: false, extraBeds: 0, cot: false, specialOccasion: false }]);

  // Step 2
  const [checkIn, setCheckIn]   = useState('');
  const [checkOut, setCheckOut] = useState('');

  // Step 3
  const [availableRooms, setAvailableRooms] = useState(null);
  const [loadingRooms, setLoadingRooms]     = useState(false);
  const [selectedTypes, setSelectedTypes]   = useState([null]);
  const [pendingCounts, setPendingCounts]   = useState({});

  // Step 4
  const [guestName, setGuestName]               = useState('');
  const [preferredContact, setPreferredContact] = useState('');
  const [countryPick, setCountryPick]           = useState({ code: '+30', flag: '🇬🇷', name: 'Greece', key: 'p0-+30-Greece' });
  const [customCode, setCustomCode]             = useState('');
  const [phoneNumber, setPhoneNumber]           = useState('');
  const [phoneError, setPhoneError]             = useState('');
  const phoneDebounceRef                        = useRef(null);
  const [email, setEmail]                       = useState('');
  const [message, setMessage]                   = useState('');
  const [submitted, setSubmitted]               = useState(false);
  const [submitting, setSubmitting]             = useState(false);
  const [submitError, setSubmitError]           = useState('');
  const [minStayBlock, setMinStayBlock]         = useState(null);
  const [honeypot, setHoneypot]                 = useState('');
  const [proofToken, setProofToken]             = useState('');
  const [pickerOpen, setPickerOpen]             = useState(false);

  const isOther     = countryPick.code === 'other';
  const dialCode    = isOther ? customCode.trim() : countryPick.code;
  const datesReady  = checkIn && checkOut && checkOut > checkIn;
  const nights      = nightCount(checkIn, checkOut);
  const allTypePicked = selectedTypes.length === roomConfigs.length && selectedTypes.every(t => t !== null);
  const cur = curSym(property?.currency);

  // Load property
  useEffect(() => {
    async function load() {
      const { data, error } = await supabase.rpc('get_property_by_slug', { p_slug: slug });
      if (error || !data || data.length === 0) { setNotFound(true); return; }
      const p = data[0];
      setProperty(p);
      Sentry.setTag('property_id', p.id);
      document.title = `${p.name} — Book Direct`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `Book directly at ${p.name}. No booking fees.`);
    }
    load();
  }, [slug]);

  // Default lang from property primary lang on first load
  useEffect(() => {
    if (!property) return;
    try {
      const saved = sessionStorage.getItem('tavelo_booking_lang');
      if (saved === 'en' || saved === 'bg') return;
    } catch {}
    if (property.lang === 'bg' || property.lang === 'en') _setLang(property.lang);
  }, [property]);

  // Available rooms when dates change
  useEffect(() => {
    if (!property || !datesReady) {
      setAvailableRooms(null); setSelectedTypes(roomConfigs.map(() => null)); return;
    }
    setLoadingRooms(true); setAvailableRooms(null);
    setSelectedTypes(roomConfigs.map(() => null));
    Promise.all([
      supabase.rpc('available_rooms', { p_property_id: property.id, p_check_in: checkIn, p_check_out: checkOut }),
      supabase.rpc('pending_room_counts', { p_property_id: property.id, p_check_in: checkIn, p_check_out: checkOut }),
    ]).then(([roomsResult, pendingResult]) => {
      const { data, error } = roomsResult;
      const counts = {};
      for (const row of (pendingResult.data || [])) {
        counts[row.room_type] = Number(row.pending_count);
      }
      setPendingCounts(counts);
      if (error) { console.error(error); setLoadingRooms(false); return; }
      const mapped = (data || []).map(r => ({
        id: r.id, name: r.name, type: r.type,
        capacity: r.capacity, basePrice: Number(r.base_price),
        seasons: r.seasons || [], sortOrder: r.sort_order,
        maxAdults: r.max_adults ?? null,
        maxChildren: r.max_children ?? null,
      }));
      const passing = mapped.filter(r => {
        const { minStay } = getMinStay(r, checkIn);
        return nights >= minStay;
      });
      if (mapped.length > 0 && passing.length === 0) {
        const rep = mapped.reduce((best, r) => {
          const { minStay } = getMinStay(r, checkIn);
          return minStay < getMinStay(best, checkIn).minStay ? r : best;
        }, mapped[0]);
        const { minStay, label } = getMinStay(rep, checkIn);
        setMinStayBlock({ minStay, label });
      } else {
        setMinStayBlock(null);
      }
      setAvailableRooms(passing);
      setLoadingRooms(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property, checkIn, checkOut]);

  // Fetch a server-signed proof token when the booking form (step 4) becomes visible
  useEffect(() => {
    if (!allTypePicked) { setProofToken(''); return; }
    fetch(
      `${import.meta.env.VITE_SUPA_URL}/functions/v1/submit-booking-request`,
      { headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPA_ANON_KEY}` } }
    )
      .then(r => r.json())
      .then(d => setProofToken(d.token ?? ''))
      .catch(() => setProofToken(''));
  }, [allTypePicked]);

  useEffect(() => () => clearTimeout(phoneDebounceRef.current), []);

  function partyForRoom(cfg) {
    return {
      adults: cfg.adults,
      children: Array.from({ length: cfg.children }, (_, j) => ({ age: cfg.childAges[j] ?? '' })),
    };
  }
  function breakfastForRoom(cfg) {
    return breakfastCost(property, partyForRoom(cfg), nights);
  }

  function addRoom() {
    setRoomConfigs(prev => [...prev, { adults: 2, children: 0, childAges: [], breakfast: false, extraBeds: 0, cot: false, specialOccasion: false }]);
    setSelectedTypes(prev => [...prev, null]);
  }
  function removeRoom(idx) {
    setRoomConfigs(prev => prev.filter((_, i) => i !== idx));
    setSelectedTypes(prev => prev.filter((_, i) => i !== idx));
  }
  function updateRoomAdults(idx, val) {
    setRoomConfigs(prev => prev.map((c, i) => i === idx ? { ...c, adults: val, extraBeds: 0, cot: false, specialOccasion: false } : c));
    setSelectedTypes(prev => prev.map((tt, i) => i === idx ? null : tt));
  }
  function updateRoomChildren(idx, val) {
    setRoomConfigs(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const ages = Array.from({ length: val }, (_, j) => c.childAges[j] ?? 0);
      return { ...c, children: val, childAges: ages, extraBeds: 0, cot: false, specialOccasion: false };
    }));
    setSelectedTypes(prev => prev.map((tt, i) => i === idx ? null : tt));
  }
  function updateChildAge(roomIdx, childIdx, val) {
    setRoomConfigs(prev => prev.map((c, i) => {
      if (i !== roomIdx) return c;
      const ages = [...c.childAges]; ages[childIdx] = val;
      return { ...c, childAges: ages };
    }));
  }
  function selectType(roomIdx, type) {
    setSelectedTypes(prev => prev.map((tt, i) => i === roomIdx ? (tt === type ? null : type) : tt));
    setRoomConfigs(prev => prev.map((c, i) => i === roomIdx ? { ...c, extraBeds: 0, cot: false, specialOccasion: false } : c));
  }

  // Estimated total for sticky band
  const estTotal = useMemo(() => {
    if (!datesReady || !availableRooms) return null;
    let total = 0;
    let roomCount = 0;
    let roomLabel = '';
    roomConfigs.forEach((cfg, i) => {
      const tt = selectedTypes[i];
      if (!tt) {
        // before pick — use cheapest available type for this config
        const opts = typesForRoom(cfg, availableRooms, selectedTypes, i, checkIn);
        if (opts.length > 0) {
          total += opts[0].cheapest * nights;
          roomCount += 1;
          if (!roomLabel) roomLabel = opts[0].type;
        }
      } else {
        const matching = availableRooms.filter(r => r.type === tt);
        if (matching.length > 0) {
          const cheapest = Math.min(...matching.map(r => getRate(r, checkIn)));
          total += cheapest * nights;
          if (cfg.breakfast) total += breakfastForRoom(cfg).total;
          total += extraBedCost(tt, cfg.extraBeds || 0, property, nights).total;
          roomCount += 1;
          if (!roomLabel) roomLabel = tt;
        }
      }
    });
    if (roomCount === 0) return null;
    const typeWord = resolveTypeLabel(roomLabel, property.room_type_info, lang).toLowerCase();
    const meta = `${t.nightFmt(nights)} · ${roomCount} ${typeWord}${roomCount > 1 ? (lang === 'bg' ? '' : 's') : ''}`;
    return { total: Math.round(total), meta };
  }, [datesReady, availableRooms, roomConfigs, selectedTypes, checkIn, nights, property, lang, t]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    if (!guestName.trim()) { setSubmitError(t.errEnterName); return; }
    if (!preferredContact) { setSubmitError(t.errPickContact); return; }
    if (!phoneNumber.trim()) { setSubmitError(t.errEnterPhone); return; }
    if (isOther && !customCode.trim()) { setSubmitError(t.errEnterDial); return; }
    const phoneErr = validatePhone(stripPhone(phoneNumber, dialCode), dialCode);
    if (phoneErr === 'letters') { setSubmitError(t.errLetters); return; }
    if (phoneErr === 'length')  { setSubmitError(t.errInvalid); return; }
    if (preferredContact === 'email' && !email.trim()) { setSubmitError(t.errEmail); return; }
    setSubmitting(true);

    const stripped  = stripPhone(phoneNumber.trim(), dialCode);
    const fullPhone = stripped ? dialCode + stripped : null;
    const totalAdults = roomConfigs.reduce((s, c) => s + c.adults, 0);
    const roomsRequested = roomConfigs.map((c, i) => ({
      adults: c.adults, children: c.children,
      childAges: c.childAges, type: selectedTypes[i],
      breakfast: c.breakfast,
      breakfast_price: c.breakfast ? breakfastForRoom(c).total : 0,
      extra_beds: c.extraBeds || 0,
      cot: c.cot || false,
      special_occasion: c.specialOccasion || false,
    }));
    const allChildren = roomConfigs.flatMap(c =>
      Array.from({ length: c.children }, (_, j) => ({ age: String(c.childAges[j] ?? '') }))
    );

    let response;
    try {
      response = await fetch(
        `${import.meta.env.VITE_SUPA_URL}/functions/v1/submit-booking-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPA_ANON_KEY}`,
          },
          body: JSON.stringify({
            proof_token: proofToken,
            honeypot,
            booking: {
              property_id:        property.id,
              guest_name:         guestName.trim(),
              phone:              fullPhone,
              email:              email.trim() || null,
              preferred_contact:  preferredContact,
              check_in:           checkIn,
              check_out:          checkOut,
              adults:             totalAdults,
              children:           allChildren,
              rooms_requested:    roomsRequested,
              message:            message.trim() || null,
              breakfast_included: roomConfigs.some(c => c.breakfast),
              breakfast_price:    roomConfigs.reduce((sum, c) => sum + (c.breakfast ? breakfastForRoom(c).total : 0), 0),
              estimated_price:    estTotal ? Math.round(estTotal.total) : null,
            },
          }),
        }
      );
    } catch {
      setSubmitting(false);
      setSubmitError(t.errGeneric);
      return;
    }
    setSubmitting(false);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const code = typeof err.error === 'string' ? err.error : '';
      let msg;
      switch (code) {
        case 'notify_failed':         msg = t.errNotifyFailed; break;
        case 'security_check_failed': msg = t.errSecurityCheck; break;
        case 'property_unavailable':  msg = t.unavailableTitle; break;
        default:                      msg = t.errGeneric;
      }
      setSubmitError(msg);
      return;
    }
    setSubmitted(true);
  }

  // ── early states ─────────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <div style={{ fontFamily: FONT.body, background: CL.sand, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏨</div>
          <div style={{ fontFamily: FONT.display, fontSize: 22, fontWeight: 500, color: CL.bark }}>{t.notFound}</div>
          <div style={{ fontSize: 13, marginTop: 8, color: CL.stone }}>{t.notFoundSub}</div>
        </div>
      </div>
    );
  }
  if (!property) {
    return (
      <div style={{ fontFamily: FONT.body, background: CL.sand, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: CL.stone, fontSize: 13 }}>{t.loading}</div>
      </div>
    );
  }

  if (property.plan_status === 'expired' || property.plan_status === 'cancelled') {
    return (
      <div style={{ minHeight:'100vh', background: CL.cream, display:'flex',
                    alignItems:'center', justifyContent:'center', padding: 24 }}>
        <div style={{ maxWidth: 540, textAlign: 'center' }}>
          <h1 style={{ fontFamily: FONT.display, color: CL.bark }}>{T[lang].unavailableTitle}</h1>
          <p style={{ color: CL.bark }}>{T[lang].unavailableBody}</p>
          {property.phone && (
            <p style={{ marginTop: 16 }}>
              <a href={`tel:${property.phone}`} style={{ color: CL.sea }}>{property.phone}</a>
            </p>
          )}
        </div>
      </div>
    );
  }

  // Success screen replaces the page
  if (submitted) {
    const summary = {
      firstName: guestName.trim().split(/\s+/)[0],
      stayLine: `${fmtF(checkIn)} → ${fmtF(checkOut)}`,
      nightsLine: t.nightFmt(nights),
      roomLine: roomConfigs.map((c, i) => {
        const tt = selectedTypes[i];
        return resolveTypeLabel(tt, property.room_type_info, lang);
      }).join(', '),
      guestsLine: roomConfigs.reduce((s, c) => s + c.adults, 0) + (roomConfigs.reduce((s, c) => s + c.children, 0) > 0 ? ` + ${roomConfigs.reduce((s, c) => s + c.children, 0)}` : ''),
      totalLine: estTotal ? `${cur}${estTotal.total}` : '',
    };
    return <SuccessScreen property={property} summary={summary} lang={lang} />;
  }

  // ── style helpers used across cards ──────────────────────────────────────────
  const cardStyle = {
    background: CL.cream, margin: '10px 14px',
    borderRadius: 14, border: '1px solid ' + CL.goldL, padding: 16,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px',
    border: '1.5px solid ' + CL.goldL, borderRadius: 9,
    fontSize: 14, fontFamily: FONT.body,
    background: '#FFFEF9', outline: 'none',
    color: CL.bark, height: 40, boxSizing: 'border-box',
  };

  // ── render ───────────────────────────────────────────────────────────────────
  const showSticky = datesReady && !!estTotal;
  return (
    <div style={{ fontFamily: FONT.body, background: CL.sand, minHeight: '100vh', color: CL.bark }}>
      <div style={{
        maxWidth: 480, margin: '0 auto',
        background: CL.sand, position: 'relative',
      }}>

        <Hero property={property} lang={lang} setLang={setLang} />
        <DirectBookingNote lang={lang} />

        {property.breakfast_enabled && property.breakfast_pricing_mode === 'included' && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 14px 0' }}>
            <span style={{
              display: 'inline-block', padding: '4px 10px',
              background: CL.olive, color: '#fff', borderRadius: 999,
              fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            }}>{t.breakfastIncluded}</span>
          </div>
        )}

        {/* ── Step 1: Your stay ── */}
        <div style={cardStyle}>
          <StepHeader n={1} title={t.yourStay} done={false} />

          {roomConfigs.map((cfg, idx) => (
            <div key={idx} style={{
              background: CL.sand, border: '1px solid ' + CL.goldL,
              borderRadius: 10, padding: 14,
              marginBottom: idx < roomConfigs.length - 1 ? 10 : 0,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 12,
              }}>
                <span style={{ fontFamily: FONT.display, fontSize: 17, fontWeight: 500, color: CL.bark }}>
                  {lang === 'bg' ? `Стая ${idx + 1}` : `Room ${idx + 1}`}
                </span>
                {roomConfigs.length > 1 && (
                  <button type="button" onClick={() => removeRoom(idx)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: CL.stone, fontSize: 18, lineHeight: 1, padding: 0,
                  }}>×</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 18, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <Lbl>{t.adults}</Lbl>
                  <Stepper value={cfg.adults} min={1} max={10} onChange={val => updateRoomAdults(idx, val)} />
                </div>
                <div>
                  <Lbl>{t.children}</Lbl>
                  <Stepper value={cfg.children} min={0} max={10} onChange={val => updateRoomChildren(idx, val)} />
                </div>
              </div>
              {cfg.children > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {Array.from({ length: cfg.children }, (_, ci) => (
                    <div key={ci}>
                      <Lbl>{fillTemplate(t.childAge, { n: ci + 1 })}</Lbl>
                      <Stepper value={cfg.childAges[ci] ?? 0} min={0} max={17} onChange={val => updateChildAge(idx, ci, val)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <button type="button" onClick={addRoom} style={{
            marginTop: 12, width: '100%', padding: 10,
            border: '1px dashed ' + CL.gold, background: 'none',
            color: CL.gold, fontSize: 11.5, fontWeight: 600,
            letterSpacing: 0.6, textTransform: 'uppercase',
            borderRadius: 10, cursor: 'pointer', fontFamily: FONT.body,
          }}>{t.addAnotherRoom}</button>
        </div>

        {/* ── Step 2: Dates ── */}
        <div style={cardStyle}>
          <StepHeader n={2} title={t.selectDates} done={!!datesReady} />
          <CalendarPicker
            checkIn={checkIn} checkOut={checkOut} lang={lang}
            onSelect={(ci, co) => {
              setCheckIn(ci); setCheckOut(co || '');
              setMinStayBlock(null); setAvailableRooms(null);
            }}
          />
        </div>

        {/* ── Step 3: Choose room types ── */}
        {datesReady && (
          <div style={cardStyle}>
            <StepHeader n={3} title={t.chooseRoomTypes} done={allTypePicked} />

            {loadingRooms && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: CL.stone, fontSize: 13 }}>
                {t.checkingAvailability}
              </div>
            )}

            {!loadingRooms && availableRooms !== null && availableRooms.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '10px 14px',
                background: '#FDF0EB', borderRadius: 10,
                color: CL.terra, fontSize: 13, fontWeight: 600,
              }}>
                {minStayBlock
                  ? fillTemplate(t.minStay, { n: minStayBlock.minStay })
                  : t.noRoomsAvailable}
              </div>
            )}

            {!loadingRooms && availableRooms !== null && availableRooms.length > 0 && roomConfigs.map((cfg, idx) => {
              const options = typesForRoom(cfg, availableRooms, selectedTypes, idx, checkIn);
              const hasOptions = options.length > 0;
              return (
                <div key={idx} style={{ marginBottom: idx < roomConfigs.length - 1 ? 16 : 0 }}>
                  <div style={{
                    fontFamily: FONT.display, fontSize: 16, fontWeight: 500,
                    color: CL.bark, marginTop: 6, lineHeight: 1,
                  }}>
                    {lang === 'bg' ? `Стая ${idx + 1}` : `Room ${idx + 1}`}
                    <small style={{
                      display: 'block', fontFamily: FONT.body, fontSize: 11,
                      color: CL.stone, fontWeight: 500, marginTop: 2,
                    }}>
                      {t.adultsFmt(cfg.adults)}{t.childrenFmt(cfg.children)}
                      {cfg.children > 0 && cfg.childAges.length > 0 && (
                        ` (${t.age} ${cfg.childAges.join(', ')})`
                      )}
                    </small>
                  </div>

                  {!hasOptions && (
                    <div style={{
                      marginTop: 8, fontSize: 13, color: CL.terra,
                      fontWeight: 600, padding: '8px 12px',
                      background: '#FDF0EB', borderRadius: 8,
                    }}>{t.noRoomTypeFor}</div>
                  )}

                  {hasOptions && options.map(opt => {
                    const isSelected = selectedTypes[idx] === opt.type;
                    const label = resolveTypeLabel(opt.type, property.room_type_info, lang);
                    const bedDesc = property.room_type_info?.[opt.type]?.bed_description;
                    const sofaInc = property.room_type_info?.[opt.type]?.sofa_included;
                    const availCount = availableRooms.filter(r => r.type === opt.type).length;
                    const pending = pendingCounts[opt.type] || 0;
                    const limited = pending >= availCount && availCount > 0;
                    return (
                      <div key={opt.type} data-testid="room-type-card" onClick={() => selectType(idx, opt.type)} style={{
                        background: isSelected ? CL.cream : '#FFFEF9',
                        border: '1.5px solid ' + (isSelected ? CL.bark : CL.goldL),
                        borderRadius: 10, padding: '12px 14px', marginTop: 8,
                        cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12,
                      }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 999, flexShrink: 0,
                          border: '1.5px solid ' + (isSelected ? CL.olive : CL.goldL),
                          marginTop: 2, position: 'relative',
                        }}>
                          {isSelected && (
                            <div style={{
                              position: 'absolute', top: 3, left: 3, width: 9, height: 9,
                              borderRadius: 999, background: CL.olive,
                            }} />
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: FONT.display, fontSize: 17, fontWeight: 500, color: CL.bark, lineHeight: 1.05 }}>
                            {label}
                          </div>
                          <div style={{ fontSize: 11, color: CL.stone, marginTop: 2 }}>
                            {t.sleepsUpTo} {opt.maxCap}{bedDesc ? ` · ${bedDesc}` : ''}
                          </div>
                          {sofaInc && (
                            <div style={{ fontSize: 11, color: CL.olive, marginTop: 3 }}>🛋 {sofaInc}</div>
                          )}
                          {limited && (
                            <div style={{
                              marginTop: 6, fontSize: 11, fontWeight: 600,
                              color: CL.terra, background: '#FDF0EB',
                              borderRadius: 6, padding: '4px 8px',
                            }}>{t.limitedAvailability}</div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontFamily: FONT.display, fontSize: 18, fontWeight: 500, color: CL.bark }}>
                            {cur}{opt.cheapest}
                          </div>
                          <small style={{
                            display: 'block', fontFamily: FONT.body, fontSize: 10,
                            color: CL.stone, fontWeight: 500, marginTop: 2,
                          }}>{t.perNight}</small>
                          {isSelected && (
                            <div style={{ fontSize: 11, color: CL.olive, fontWeight: 700, marginTop: 4 }}>
                              {t.selected}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Per-room extras (cot, sofa bed, special occasion, breakfast) */}
                  {selectedTypes[idx] && (() => {
                    const type = selectedTypes[idx];
                    const { pricePerNight: ebPrice } = extraBedCost(type, 0, property, 1);
                    const sofaIsIncluded = !!property.room_type_info?.[type]?.sofa_included;
                    const showSofaBed = !sofaIsIncluded && ebPrice > 0;
                    const tile = (active) => ({
                      padding: '10px 14px', borderRadius: 10, textAlign: 'left',
                      fontFamily: 'inherit', cursor: 'pointer', width: '100%',
                      border: '1px solid ' + (active ? CL.olive : CL.goldL),
                      background: active ? CL.cream : '#FFFEF9',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    });
                    const cb = (active) => ({
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: '2px solid ' + (active ? CL.olive : CL.goldL),
                      background: active ? CL.olive : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    });
                    const showBreakfast = property.breakfast_enabled
                      && property.breakfast_pricing_mode !== 'included'
                      && property.breakfast_pricing_mode !== 'none';
                    const bk  = showBreakfast ? breakfastForRoom(cfg) : null;
                    const bkUnit = Number(property.breakfast_price_per_person) || 0;
                    return (
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {cfg.children > 0 && (
                          <button type="button"
                            onClick={() => setRoomConfigs(prev => prev.map((c, j) => j === idx ? { ...c, cot: !c.cot } : c))}
                            style={tile(cfg.cot)}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: CL.bark }}>{t.babyCot}</div>
                              <div style={{ fontSize: 11, color: CL.stone, marginTop: 2 }}>{t.cotMeta}</div>
                            </div>
                            <div style={cb(cfg.cot)}>{cfg.cot && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}</div>
                          </button>
                        )}
                        {showSofaBed && (
                          <button type="button"
                            onClick={() => setRoomConfigs(prev => prev.map((c, j) => j === idx ? { ...c, extraBeds: c.extraBeds ? 0 : 1 } : c))}
                            style={tile(cfg.extraBeds > 0)}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: CL.bark }}>{t.sofaBed}</div>
                              <div style={{ fontSize: 11, color: CL.stone, marginTop: 2 }}>{cur}{ebPrice}/{t.night}</div>
                            </div>
                            <div style={cb(cfg.extraBeds > 0)}>{cfg.extraBeds > 0 && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}</div>
                          </button>
                        )}
                        <button type="button"
                          onClick={() => setRoomConfigs(prev => prev.map((c, j) => j === idx ? { ...c, specialOccasion: !c.specialOccasion } : c))}
                          style={tile(cfg.specialOccasion)}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: CL.bark }}>{t.specialOccasion}</div>
                          </div>
                          <div style={cb(cfg.specialOccasion)}>{cfg.specialOccasion && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}</div>
                        </button>
                        {showBreakfast && bk && (
                          <div style={{
                            padding: '10px 14px', background: CL.cream,
                            border: '1px solid ' + CL.goldL, borderRadius: 10,
                          }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: CL.bark }}>
                              <input type="checkbox" checked={cfg.breakfast}
                                onChange={e => setRoomConfigs(prev => prev.map((c, j) => j === idx ? { ...c, breakfast: e.target.checked } : c))} />
                              <span>{t.addBreakfast}</span>
                            </label>
                            <div style={{ marginTop: 4, fontSize: 11, color: CL.stone, paddingLeft: 26 }}>
                              {property.breakfast_pricing_mode === 'per_room'
                                ? `${cur}${bkUnit}/${t.night} × ${nights} = ${cur}${bk.total}`
                                : `${cur}${bkUnit} pp/${t.night} × ${bk.eligibleCount} × ${nights} = ${cur}${bk.total}`}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Step 4: Your details ── */}
        {allTypePicked && (
          <form onSubmit={handleSubmit} autoComplete="on">
          <div style={cardStyle}>
            {/* honeypot */}
            <div style={{ position: 'absolute', left: -9999, opacity: 0, height: 0, overflow: 'hidden' }}>
              <input type="text" name="website" value={honeypot}
                onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
            </div>

            <StepHeader n={4} title={t.yourDetails} done={false} />

            <div style={{ marginBottom: 14 }}>
              <Lbl>{t.fullName} *</Lbl>
              <input value={guestName} onChange={e => setGuestName(e.target.value)}
                placeholder={t.placeholderName} style={inputStyle} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <Lbl>{t.preferredContact} *</Lbl>
              <span style={{
                display: 'flex', border: '1px solid ' + CL.goldL,
                borderRadius: 999, overflow: 'hidden', background: CL.cream,
              }}>
                {['phone', 'whatsapp', 'viber', 'email'].map(ch => {
                  const on = preferredContact === ch;
                  return (
                    <button key={ch} type="button" onClick={() => setPreferredContact(ch)} style={{
                      flex: 1, background: on ? CL.bark : 'transparent',
                      border: 'none', padding: '7px 6px',
                      fontSize: 11, fontWeight: 600,
                      color: on ? CL.gold : CL.stone,
                      letterSpacing: 0.5, textTransform: 'uppercase',
                      cursor: 'pointer', fontFamily: FONT.body,
                    }}>
                      {ch === 'phone' ? t.phone : ch === 'whatsapp' ? t.whatsapp : ch === 'viber' ? t.viber : t.email}
                    </button>
                  );
                })}
              </span>
            </div>

            <div style={{ marginBottom: 14 }}>
                <Lbl>{t.phoneNumber} *</Lbl>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => setPickerOpen(true)} style={{
                    padding: '10px 12px', border: '1.5px solid ' + CL.goldL,
                    borderRadius: 9, background: '#FFFEF9',
                    fontSize: 13.5, fontFamily: FONT.body, color: CL.bark,
                    fontWeight: 500, width: 108, textAlign: 'left', cursor: 'pointer',
                  }}>
                    <span>{countryPick.flag}</span>{' '}
                    <span>{isOther ? (customCode || '+XX') : countryPick.code}</span>{' '}
                    <span style={{ color: CL.stone }}>▾</span>
                  </button>
                  {isOther && (
                    <input type="text" value={customCode}
                      onChange={e => {
                        clearTimeout(phoneDebounceRef.current);
                        setPhoneError('');
                        setCustomCode(e.target.value);
                      }}
                      placeholder={t.placeholderCustomCode}
                      style={{ ...inputStyle, width: 64, padding: '10px 8px', fontSize: 14 }}
                    />
                  )}
                  <input type="tel" value={phoneNumber} autoComplete="tel"
                    onChange={e => {
                      const val = e.target.value;
                      setPhoneNumber(val);
                      setPhoneError('');
                      clearTimeout(phoneDebounceRef.current);
                      phoneDebounceRef.current = setTimeout(() => {
                        if (!val.trim()) return;
                        const dial = isOther ? customCode.trim() : countryPick.code;
                        const err = validatePhone(stripPhone(val, dial), dial);
                        if (err === 'letters') setPhoneError(t.errLetters_inline);
                        if (err === 'length')  setPhoneError(t.errInvalid_inline);
                      }, 600);
                    }}
                    placeholder={PHONE_PLACEHOLDERS[countryPick.code] ?? '887 123 456'}
                    style={{
                      ...inputStyle, flex: 1,
                      borderColor: phoneError ? CL.terra : CL.goldL,
                    }}
                  />
                </div>
                {phoneError && (
                  <div style={{ fontSize: 12, color: CL.terra, marginTop: 4 }}>{phoneError}</div>
                )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <Lbl optional={preferredContact === 'email' ? null : t.optional}>
                {t.email}{preferredContact === 'email' ? ' *' : ''}
              </Lbl>
              <input type="email" value={email} autoComplete="email"
                onChange={e => setEmail(e.target.value)}
                placeholder={t.placeholderEmail} style={inputStyle} />
            </div>

            <div style={{ marginBottom: 6 }}>
              <Lbl optional={t.optional}>{t.message}</Lbl>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder={t.placeholderMessage} rows={3}
                style={{
                  ...inputStyle, height: 'auto', minHeight: 60,
                  resize: 'vertical', padding: '10px 12px',
                }} />
            </div>

            {/* Captcha — sand-tinted card around the Cloudflare Turnstile widget.
                The widget renders its own "I'm not a robot" UI; we just provide
                the surrounding card to match the design language. */}
            {submitError && (
              <div style={{ color: CL.terra, fontSize: 13, marginBottom: 12, fontWeight: 600 }}>{submitError}</div>
            )}
          </div>

          {/* Submit button + post-note (sand-tinted help) */}
          <div style={{ padding: 14, background: CL.sand }}>
            <button type="submit" data-testid="submit-booking-btn" disabled={submitting} style={{
              width: '100%', padding: 14,
              background: submitting ? CL.stone : CL.olive,
              color: '#fff', border: 'none', borderRadius: 11,
              fontFamily: FONT.body, fontWeight: 600, fontSize: 15,
              letterSpacing: 0.4, cursor: submitting ? 'default' : 'pointer',
            }}>{submitting ? t.sending : t.requestBooking}</button>
            <div style={{
              marginTop: 12, padding: '11px 14px',
              background: CL.cream, border: '1px solid ' + CL.goldL,
              borderRadius: 8, fontSize: 11.5, color: CL.stone, lineHeight: 1.5,
            }}>{t.postNote}</div>
          </div>
          </form>
        )}

        <FooterBlock property={property} lang={lang} />

        {showSticky && (
          <StickyTotal amount={`${cur}${estTotal.total}`} meta={estTotal.meta} lang={lang} />
        )}

      </div>

      <CountryPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedKey={countryPick.key}
        onPick={(p) => {
          setCountryPick({ code: p.code, flag: p.flag, name: p.name, key: p.key });
          setPhoneError('');
          if (p.code !== 'other') setCustomCode('');
          setPickerOpen(false);
        }}
        lang={lang}
      />
    </div>
  );
}
